import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AbstractWallet, ETHEREUM_METHOD, LOCAL_STORAGE } from '@blink/components/src/utils';
import { Steps } from 'antd';
import { ethers, parseUnits } from 'ethers';
import { ALLOWANCE_STATUS, WALLET } from '@blink/components/src/types';
import { Api } from '@blink/components';
import { useNavigate } from 'react-router-dom';
import { NETWORK } from '@blink/components/src/constants/global';

import { Allowance } from './Allowance/Allowance';
import { useRegisterMarginAccountStyle } from './RegisterMarginAccountModal.style';

import { useHideModal, useModal } from '@/features/Modals/hooks';
import { SelectedBorrowAsset } from '@/features/Modals/ui/BorrowModal/BorrowModal';
import { Store } from '@/shared/store';
import { showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import IERCAbi from '@/shared/abi/IERCAbi.json';
import DispatcherABI from '@/shared/abi/DispatcherABI.json';
import { getSafeContract } from '@/shared/utils/getSafeContract';
import { getSafeSigner } from '@/shared/utils/getSafeSigner';
import { toast } from '@/shared/hooks/useToast';
import { getWalletMap } from '@/shared/utils/getWalletMap';
import { Button, ButtonVariant, ViewOnEtherscanButton } from '@/shared/ui';
import { OperationStatusType } from '@/features/Modals';

const registerAccountErrors = new Map()
    .set('0x91cab504', 'Nonce already used')
    .set('0x0819bdcd', 'Signature expired')
    .set('0xda9670b6', 'Signer is now allowed');

type Collateral = {
    token: string;
    amount: bigint;
};

export const getSigner = async (type: WALLET, blockchainName: NETWORK) => {
    const walletMap = getWalletMap();
    const wallet: AbstractWallet = walletMap.get(type) as AbstractWallet;
    const walletProvider = await wallet.getProvider();

    await walletProvider.request({ method: ETHEREUM_METHOD.REQUEST });

    const signer = await getSafeSigner(walletProvider, blockchainName);
    return signer;
};

export const RegisterMarginAccountModal: FC = () => {
    const classes = useRegisterMarginAccountStyle();

    const { modalProps } = useModal();
    const { handleCloseModal } = useHideModal();
    const {
        agreementAddress,
        selectedAssets = [],
        tokenSymbol,
        assetAmount,
        assetAddress,
        refresh,
    } = modalProps || {};
    const newSelectedAssetsStatuses: ALLOWANCE_STATUS[] = [];
    for (let i = 0; i < selectedAssets?.length; i++) {
        const isFirst = i === 0;
        const status = isFirst ? ALLOWANCE_STATUS.PROCESSING : ALLOWANCE_STATUS.WAITING;
        newSelectedAssetsStatuses.push(status);
    }
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [selectedAssetsStatuses, setSelectedAssetsStatuses] =
        useState<ALLOWANCE_STATUS[]>(newSelectedAssetsStatuses);
    const [walletSigner, setWalletSigner] = useState<ethers.JsonRpcSigner>();
    const [isInProgress, setIsInProgress] = useState<boolean>(true);
    const { type, blockchainName } = useSelector((state: Store) => ({
        blockchainName: state.wallets.network || LOCAL_STORAGE.NETWORK,
        type: state.wallets.active.type,
    }));
    const current = selectedAssetsStatuses.findIndex(
        (assetStatus) =>
            assetStatus === ALLOWANCE_STATUS.PROCESSING ||
            assetStatus === ALLOWANCE_STATUS.WARNING ||
            assetStatus === ALLOWANCE_STATUS.ERROR,
    );
    const isDone = !selectedAssetsStatuses.some(
        (assetStatus) => assetStatus !== ALLOWANCE_STATUS.SUCCESS,
    );

    const getAssetNames = () => {
        if (selectedAssets.length > 2) {
            return (
                selectedAssets[0].amount +
                ' ' +
                selectedAssets[0].symbol +
                ' / ' +
                selectedAssets[1].amount +
                ' ' +
                selectedAssets[1].symbol +
                ' and ' +
                (selectedAssets.length - 2) +
                ' more'
            );
        }

        return selectedAssets
            ?.map((asset: SelectedBorrowAsset) => `${asset.amount} ${asset.symbol}`)
            .join(' / ');
    };

    const getBorrowAmount = () => {
        return assetAmount + ' ' + tokenSymbol;
    };

    useEffect(() => {
        (async () => {
            if (type && blockchainName) {
                const signer = await getSigner(type, blockchainName);
                setWalletSigner(signer as ethers.JsonRpcSigner);
            }
        })();
    }, [type, blockchainName]);

    useEffect(() => {
        if (!isDone || !walletSigner || !isInProgress) {
            return;
        }
        (async () => {
            try {
                const dispatcherAddress = Api.APP_DISPATCHER_ADDRESS as string;
                const dispatcher = getSafeContract(dispatcherAddress, DispatcherABI, walletSigner);
                const tokenAddress: string = assetAddress;
                const selectedAssetContract = getSafeContract(tokenAddress, IERCAbi, walletSigner);
                const selectedAssetDecimal = await selectedAssetContract.decimals();

                const collateral: Collateral[] = [];

                for (const selectedAsset of selectedAssets) {
                    const tokenSelectedAssets: string = selectedAsset.address;
                    const token = getSafeContract(tokenSelectedAssets, IERCAbi, walletSigner);

                    const decimal = await token.decimals();
                    const amount = parseUnits(selectedAsset.amount.toString(), decimal);

                    collateral.push({
                        token: tokenSelectedAssets,
                        amount,
                    });

                    const allowance: bigint = await token.allowance(
                        walletSigner.address,
                        dispatcherAddress,
                    );

                    if (allowance < amount) {
                        const approveToken = await token.approve(dispatcherAddress, amount);
                        await approveToken.wait();
                    }
                }

                const leverageAmount = parseUnits(assetAmount.toString(), selectedAssetDecimal);
                const leverage = { amount: leverageAmount, token: tokenAddress };
                const tx = await dispatcher.registerAccount(agreementAddress, collateral, leverage);

                handleCloseModal();
                navigate('/borrow');

                dispatch(
                    showModalAction({
                        modalName: ModalName.OPERATION_STATUS_MODAL,
                        modalProps: {
                            type: isDone && OperationStatusType.Success,
                            description: (
                                <>
                                    {`Supplied: ${getAssetNames()} → Borrowed: ${getBorrowAmount()}`}
                                    <ViewOnEtherscanButton
                                        resource={{ tx: tx.hash }}
                                        className='mt-[24px] text-[16px] [&_svg]:size-[24px] text-content-accent'
                                    />
                                </>
                            ),
                            closable: true,
                            withoutButton: true,
                        },
                    }),
                );
            } catch (e: any) {
                console.warn(e);
                let messageError = 'Transaction was rejected';
                const errorData = e?.data;
                setIsInProgress(false);
                if (errorData) {
                    const errorSelector = errorData.substring(0, 10);
                    messageError = registerAccountErrors.has(errorSelector)
                        ? registerAccountErrors.get(errorSelector)
                        : messageError;
                }
                toast({
                    title: messageError,
                    variant: 'error',
                });
            } finally {
                if (refresh) {
                    await refresh();
                }
            }
        })();
    }, [isDone, isInProgress]);

    if (!walletSigner || !type || !blockchainName) {
        return <div>Loading</div>;
    }

    const items = selectedAssets.map((selectedAsset: SelectedBorrowAsset, index: number) => {
        const lastDone = selectedAssetsStatuses.lastIndexOf(ALLOWANCE_STATUS.SUCCESS) === index;

        const onChangeStatusHandler = (status: ALLOWANCE_STATUS) => {
            const copyAllSelectedAssets = [...selectedAssetsStatuses];
            copyAllSelectedAssets[index] = status;
            if (index !== selectedAssets.length - 1 && status === ALLOWANCE_STATUS.SUCCESS) {
                copyAllSelectedAssets[index + 1] = ALLOWANCE_STATUS.PROCESSING;
            }
            setSelectedAssetsStatuses(copyAllSelectedAssets);
        };

        const allowanceStatus = selectedAssetsStatuses[index];

        return {
            description: (
                <Allowance
                    key={selectedAsset.name}
                    asset={selectedAsset.symbol}
                    assetAddress={selectedAsset.address}
                    requiredAmount={selectedAsset.amount}
                    status={allowanceStatus}
                    changeStatus={onChangeStatusHandler}
                    signer={walletSigner}
                    lastItem={lastDone}
                />
            ),
        };
    });

    const tryAgainHandler = () => {
        setIsInProgress(true);
    };

    const getActions = () => {
        return items.length && items.length > 1 ? (
            <Steps
                direction='vertical'
                current={current === -1 ? selectedAssetsStatuses.length - 1 : current}
                status={isDone ? 'finish' : 'process'}
                items={items}
            />
        ) : (
            items[0]?.description
        );
    };

    return (
        <>
            <div className={classes.root}>{getActions()}</div>
            {selectedAssets && (
                <div className='flex items-start gap-[8px] self-stretch px-[16px] py-[12px] rounded-[4px] bg-background-brand50 text-content-brand500'>
                    <p>
                        By confirming, you authorize Arkis to execute these transactions on your
                        behalf.
                    </p>
                </div>
            )}
            <div className='mt-5'>
                <Button
                    variant={ButtonVariant.Primary}
                    onClick={tryAgainHandler}
                    disabled={isInProgress}
                    className='w-full text-[16px] h-[48px]'
                >
                    Processing
                </Button>
            </div>
        </>
    );
};
